// Dashboard card: yearly artist ranking by realized services, with medal
// colors for the top three and year navigation.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, Divider, Flex, Spinner, Text, useColorModeValue } from "@chakra-ui/react";
import { MdEmojiEvents } from "react-icons/md";
import Card from "horizon/components/card/Card";
import CardHeader from "../shared/CardHeader";
import YearNavFooter from "../shared/YearNavFooter";

/** Scrollable list style for the ranking rows. */
const SCROLL_SX = {
  overflowY: "auto", maxHeight: "420px",
  "::-webkit-scrollbar": { width: "6px" },
  "::-webkit-scrollbar-thumb": { background: "rgba(163,174,208,0.4)", borderRadius: "10px" }
};

/**
 * Returns the medal color for a podium position, or null past the top three.
 * @param {number} index - zero-based rank index
 * @returns {string|null} a hex color for ranks 0-2, otherwise null
 */
function getMedalColor(index) {
  return ["#FFD700", "#C0C0C0", "#CD7F32"][index] || null;
}

/**
 * Renders a single ranking row: position, artist name and realized count.
 * @param {Object} props - index (number), value ({artist:{name}, service_realized})
 * @returns {React.ReactElement}
 */
function RankingRow({ index, value }) {
  const rowBg = useColorModeValue("secondaryGray.300", "navy.700");
  const nameColor = useColorModeValue("secondaryGray.900", "white");
  const medal = getMedalColor(index);
  return (
    <Flex align="center" justify="space-between" bg={rowBg} borderRadius="12px" px="14px" py="10px" mb="8px">
      <Text w="15%" fontWeight="700" color={medal || nameColor}>{index + 1}</Text>
      <Text w="55%" textAlign="center" noOfLines={1} fontWeight="600" color={nameColor}>
        {value?.artist?.name}
      </Text>
      <Text w="30%" textAlign="right" fontWeight="700" color="red.500">{value?.service_realized}</Text>
    </Flex>
  );
}

/**
 * Displays the artist ranking for a given year, allowing year navigation.
 * @returns {React.ReactElement}
 */
export default function ArtistsRankingCard() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState([]);

  const load = useCallback(async (targetYear) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/admin/reservation/artist_ranking/${targetYear}`);
      setRanking(data || []);
    } catch (error) {
      console.error("Error fetching artist ranking:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeYear = useCallback((delta) => {
    const next = year + delta;
    setYear(next);
    load(next);
  }, [year, load]);

  useEffect(() => {
    load(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Card h="100%">
      <CardHeader icon={MdEmojiEvents} title={`Classement ${year}`} accent="orange.400" />
      <Divider mb="8px" />
      <Flex justify="space-between" px="14px" mb="8px">
        <Text w="15%" fontSize="xs" fontWeight="600" color={headColor}>Rang</Text>
        <Text w="55%" fontSize="xs" fontWeight="600" color={headColor} textAlign="center">Nom</Text>
        <Text w="30%" fontSize="xs" fontWeight="600" color={headColor} textAlign="right">Nb.R</Text>
      </Flex>
      {loading
        ? <Flex justify="center" py="6"><Spinner color="orange.400" /></Flex>
        : ranking.length
          ? <Box sx={SCROLL_SX}>{ranking.map((v, i) => <RankingRow key={i} index={i} value={v} />)}</Box>
          : <Text textAlign="center" py="6" fontSize="sm" color={headColor}>Aucun artiste à afficher</Text>}
      <YearNavFooter loading={loading} onPrev={() => changeYear(-1)} onNext={() => changeYear(1)}
        onRefresh={() => load(year)} />
    </Card>
  );
}
